'use client';
import type { Menu } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { CategoriesEntity } from '@eliadmin/core/src/api/products/types/product-types';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import Link from '@eliadmin/core/src/components/link';
import { FiChevronDown } from 'react-icons/fi';

type HomepageCategoryFilterProps = {
  className?: {
    wrapper?: string;
  };
  selectedCategory: CategoriesEntity | null;
  categories: CategoriesEntity[];
  menu: Menu[];
  category: Category | null;
};

export const HomepageCategoryFilter = ({
  className,
  selectedCategory,
  categories,
  menu,
}: HomepageCategoryFilterProps) => {
  const { t } = useTranslations();

  const { isOpen, setIsOpen } = useDisclosure(true);
  const { isOpen: isOpenAdvisory, setIsOpen: setIsOpenAdvisory } = useDisclosure(false);

  return (
    <div className={cn('', '', className?.wrapper)}>
      <Collapsible open={isOpen}>
        <CollapsibleTrigger
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full justify-between pr-6 font-semibold"
        >
          <p>{t('text176')}</p>
          <span
            className={cn(
              'text-foreground transition-transform duration-100 ease-linear',
              isOpen && 'rotate-180',
            )}
          >
            <FiChevronDown className={'h-6 w-6'} />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {categories.map(category => (
            <CategoryFilterItem
              category={category}
              key={category.id}
              selected={selectedCategory?.slug === category.slug}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>
      <Separator className="my-2 max-w-[90%] bg-black" />
      {menu[0].fields.map((item, index) => (
        <div key={index}>
          {item.childs && (
            <Collapsible open={isOpenAdvisory}>
              <div className="flex w-full justify-between pr-6 font-semibold">
                <Link href={item.path ?? '#'} className={cn('font-semibold hover:underline')}>
                  {item.name}
                </Link>
                <CollapsibleTrigger
                  onClick={() => setIsOpenAdvisory(!isOpenAdvisory)}
                  aria-label={item.name}
                  className="flex flex-1 justify-end"
                >
                  <span
                    className={cn(
                      'text-foreground transition-transform duration-100 ease-linear',
                      isOpenAdvisory && 'rotate-180',
                    )}
                  >
                    <FiChevronDown className={'h-6 w-6'} />
                  </span>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                {item.childs.map((category, index) => (
                  <CategoryFilterItem
                    category={category}
                    key={index}
                    selected={selectedCategory?.slug === category.slug}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {!item.childs && (
            <Link href={item.path ?? '#'} className={cn('font-semibold hover:underline')}>
              {item.name}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

const CategoryFilterItem = ({
  category,
  selected,
}: {
  category: CategoriesEntity;
  selected: boolean;
}) => {
  return (
    <div>
      <Link
        href={category.slug ?? '#'}
        className={cn('hover:underline', selected && 'font-semibold')}
      >
        {category.name}
      </Link>
    </div>
  );
};
